import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Briefcase, ArrowLeft, MapPin, Mail, Phone, Globe, ExternalLink, 
  Eye, MousePointerClick, Loader2, Search, Filter, X, Building2
} from 'lucide-react';
import { useLocation } from '../context/LocationContext';
import Breadcrumbs from '../components/Breadcrumbs';
import useTenantGeoRedirect from '../hooks/useTenantGeoRedirect';
// Use centralized API service
import { municipiosApi, jobsApi } from '../services/api';

// Job List Item Component
const JobListItem = ({ job, onTrackView, onTrackClick }) => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Track view when job becomes visible
    onTrackView(job.id);
  }, [job.id, onTrackView]);

  const handleLinkClick = async (channel, link) => {
    // Track click
    await onTrackClick(job.id, channel);
    
    // Open link
    if (channel === 'email') {
      window.location.href = `mailto:${link}`;
    } else if (channel === 'whatsapp') {
      const formattedPhone = link.replace(/\D/g, '');
      window.open(`https://wa.me/${formattedPhone}`, '_blank');
    } else if (channel === 'website') {
      const url = link.startsWith('http') ? link : `https://${link}`;
      window.open(url, '_blank');
    }
  };

  const truncateDescription = (text, maxLength = 200) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const links = job.application_links || {};

  return (
    <div 
      className="bg-white border border-gray-200 rounded-xl p-4 hover:border-teal-300 transition-all"
      data-testid={`job-list-item-${job.id}`}
    >
      <div className="flex items-start gap-4">
        {/* Business Avatar */}
        <div 
          className="w-12 h-12 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0 cursor-pointer"
          onClick={() => navigate(`/negocio/${job.business_id}`)}
        >
          {job.business_avatar ? (
            <img 
              src={job.business_avatar} 
              alt={job.business_name} 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Building2 className="w-6 h-6 text-gray-400" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Business Info */}
          <button
            onClick={() => navigate(`/negocio/${job.business_id}`)}
            className="text-sm text-teal-600 hover:underline font-medium flex items-center gap-1"
          >
            {job.business_name}
            {job.business_bairro && (
              <span className="text-gray-400 font-normal flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {job.business_bairro}
              </span>
            )}
          </button>

          {/* Job Title */}
          <h3 className="font-bold text-lg text-gray-900 mt-1">{job.titulo}</h3>

          {/* Job Description */}
          <p className="text-gray-600 text-sm mt-2 leading-relaxed">
            {truncateDescription(job.descricao)}
          </p>

          {/* Application Links */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            {links.whatsapp && (
              <button
                onClick={() => handleLinkClick('whatsapp', links.whatsapp)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg font-medium text-sm hover:bg-green-600 transition-colors"
                data-testid={`job-${job.id}-whatsapp`}
              >
                <Phone className="w-4 h-4" />
                WhatsApp
              </button>
            )}
            {links.email && (
              <button
                onClick={() => handleLinkClick('email', links.email)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg font-medium text-sm hover:bg-blue-600 transition-colors"
                data-testid={`job-${job.id}-email`}
              >
                <Mail className="w-4 h-4" />
                E-mail
              </button>
            )}
            {links.website && (
              <button
                onClick={() => handleLinkClick('website', links.website)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg font-medium text-sm hover:bg-gray-800 transition-colors"
                data-testid={`job-${job.id}-website`}
              >
                <Globe className="w-4 h-4" />
                Site
                <ExternalLink className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Vagas() {
  const navigate = useNavigate();
  const { municipioSlug } = useParams();
  const { location } = useLocation();
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [trackedViews, setTrackedViews] = useState(new Set());
  const [municipio, setMunicipio] = useState(null);

  // Auto-redirect /vagas → /<slug>/vagas (user choice → cached geo → fresh geo)
  useTenantGeoRedirect({ routeName: 'vagas', currentSlug: municipioSlug });

  // Load municipality info
  useEffect(() => {
    const loadMunicipio = async () => {
      if (municipioSlug) {
        try {
          const allMunicipios = await municipiosApi.list();
          const found = allMunicipios.find(m => m.slug === municipioSlug);
          if (found) setMunicipio(found);
        } catch (e) {
          console.error('Error loading municipio:', e);
        }
      }
    };
    loadMunicipio();
  }, [municipioSlug]);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      let jobsData = [];
      
      // First try with municipality filter if available
      if (municipioSlug) {
        jobsData = await jobsApi.listPublic({ municipio_slug: municipioSlug, limit: 100 });
      }
      
      // If no jobs in municipality or no municipality filter, get all jobs
      if (jobsData.length === 0) {
        jobsData = await jobsApi.listPublic({ limit: 100 });
      }
      
      setJobs(jobsData);
    } catch (error) {
      console.error('Erro ao carregar vagas:', error);
    } finally {
      setLoading(false);
    }
  }, [municipioSlug]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const trackView = useCallback(async (jobId) => {
    // Only track once per session
    if (trackedViews.has(jobId)) return;
    
    try {
      await jobsApi.trackView(jobId);
      setTrackedViews(prev => new Set([...prev, jobId]));
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  }, [trackedViews]);

  const trackClick = useCallback(async (jobId, channel) => {
    try {
      await jobsApi.trackClick(jobId, channel);
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  }, []);

  // Filter jobs by search query
  const filteredJobs = jobs.filter(job => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      job.titulo.toLowerCase().includes(query) ||
      job.descricao.toLowerCase().includes(query) ||
      job.business_name?.toLowerCase().includes(query) ||
      job.business_bairro?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header with Breadcrumbs */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Breadcrumbs 
            items={[
              { label: 'Vagas de Emprego', path: null }
            ]}
            municipio={municipio}
            className="mb-2"
          />
          <div className="flex items-center gap-2">
            <Briefcase className="w-6 h-6" />
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
              Vagas de Emprego
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar vagas por cargo, empresa ou bairro..."
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              data-testid="jobs-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="mb-6 flex items-center justify-between">
          <p className="text-gray-600">
            <span className="font-bold text-teal-600">{filteredJobs.length}</span>
            {' '}{filteredJobs.length === 1 ? 'vaga encontrada' : 'vagas encontradas'}
          </p>
        </div>

        {/* Jobs List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-16">
            <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-700 mb-2">
              {searchQuery ? 'Nenhuma vaga encontrada' : 'Nenhuma vaga disponível'}
            </h3>
            <p className="text-gray-500">
              {searchQuery 
                ? 'Tente buscar por outros termos' 
                : 'No momento não há vagas abertas nesta região'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredJobs.map((job, idx) => (
              <JobListItem
                key={`${job.id || job._id || 'job'}-${idx}`}
                job={job}
                onTrackView={trackView}
                onTrackClick={trackClick}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
